import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '@/core/AuthContext';
import { db } from '@/core/firebase';
import { Button, Card } from '../../../components/ui';
import { SEOHead } from '../../../components/SEOHead';
import { TrainerProfilePhotoUpload } from '../../../components/trainer/TrainerProfilePhotoUpload';

export function TrainerOnboarding() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState(userData?.displayName || '');
  const [photoURL, setPhotoURL] = useState('');
  const [bio, setBio] = useState('');
  const [certifications, setCertifications] = useState('');
  const [saving, setSaving] = useState(false);

  const totalSteps = 2;
  const trainerId = useMemo(
    () => userData?.trainerProfileId || userData?.trainerId || user?.uid || '',
    [userData, user]
  );

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleComplete = async () => {
    if (!user || !trainerId || !displayName.trim()) return;

    setSaving(true);
    try {
      const trimmedBio = bio.trim().slice(0, 500);
      const trimmedCertifications = certifications.trim();

      await setDoc(
        doc(db, 'trainers', trainerId),
        {
          name: displayName.trim(),
          photoURL: photoURL.trim(),
          bio: trimmedBio,
          certifications: trimmedCertifications,
          ownerUserId: user.uid,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, 'users', user.uid),
        {
          displayName: displayName.trim(),
          trainerProfileId: trainerId,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );

      navigate('/trainer');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Failed to save your information. Please try again.');
      setSaving(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return true;
    if (step === 2) return displayName.trim().length > 0;
    return false;
  };

  return (
    <>
      <SEOHead
        title="Trainer Onboarding | Start Grounded"
        description="Welcome to Start Grounded - set up your trainer profile"
        canonicalUrl="https://startgrounded.app/trainer/onboarding"
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl" padding="lg" variant="elevated">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">
                Step {step} of {totalSteps}
              </span>
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                {Math.round((step / totalSteps) * 100)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
                style={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {/* Step 1: Welcome */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Welcome to Start Grounded!
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400">
                    Let's get your trainer profile set up
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Name */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    What should clients call you?
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400">
                    Add the basics clients should see when they open your profile
                  </p>
                </div>
                <div className="space-y-5">
                  {trainerId && (
                    <TrainerProfilePhotoUpload
                      trainerId={trainerId}
                      value={photoURL}
                      onChange={setPhotoURL}
                    />
                  )}

                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Your name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g., Coach Sarah"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
                    autoFocus
                    data-testid="name-input"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                      Bio
                    </label>
                    <span className="text-xs text-gray-500 dark:text-slate-500">
                      {bio.length}/500
                    </span>
                  </div>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value.slice(0, 500))}
                    rows={5}
                    placeholder="Tell clients how you coach, what you focus on, and what it feels like to train with you."
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition resize-none"
                    data-testid="trainer-bio-input"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Certifications
                    </label>
                    <input
                      type="text"
                      value={certifications}
                      onChange={(e) => setCertifications(e.target.value)}
                      placeholder="NASM-CPT, CrossFit L2, Precision Nutrition"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
                      data-testid="trainer-certifications-input"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-slate-500">
                      Keep certifications separate from your bio so clients can scan them quickly.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {step < totalSteps ? (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1"
                data-testid="continue-button"
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleComplete}
                disabled={!canProceed() || saving}
                isLoading={saving}
                className="flex-1"
                data-testid="save-button"
              >
                {saving ? 'Saving...' : 'Complete Setup'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
